import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useBackend } from '../backend/BackendContext';
import { ensureProfile, readProfile } from '../store/profile-store';
import { cellarEntry, listCellars, type CellarEntry } from './cellar-list';
import type { Person } from './session';

interface CellarListValue {
  /**
   * The cellars this person may open: only their own for a member (who may not even list the
   * others, infra/lib/permissions.ts); for the owner their own then everyone else's (T070).
   * Undefined while the owner's list is loading.
   */
  cellars: CellarEntry[] | undefined;
  /**
   * Reloads the list (e.g. when the selector opens, to pick up a new person's cellar, or once a
   * cellar is renamed).
   */
  refresh: () => void;
}

const CellarListContext = createContext<CellarListValue | undefined>(undefined);

/**
 * Runs once per signed-in person: creates their profile.json on first sign-in
 * (contracts/storage-and-permissions.md), then, for the owner, lists the cellars.
 */
export function CellarListProvider({ person, children }: { person: Person; children: ReactNode }) {
  const { storage } = useBackend();
  const own = useMemo<CellarEntry[]>(() => [{ id: person.id, name: '', own: true }], [person.id]);
  const [cellars, setCellars] = useState<CellarEntry[] | undefined>(
    person.role === 'owner' ? undefined : own,
  );
  const [generation, setGeneration] = useState(0);
  const [profileReady, setProfileReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // A failed profile write is retried at the next sign-in; it never blocks the person's cellar.
    void ensureProfile(storage, person.id, person.email)
      .catch(() => undefined)
      .then(() => {
        if (!cancelled) setProfileReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [storage, person]);

  // A member's list is their own cellar, named as they named it (owner request, 2026-10-08).
  useEffect(() => {
    if (person.role === 'owner' || !profileReady) return;
    let cancelled = false;
    void readProfile(storage, person.id)
      .catch(() => undefined)
      .then((profile) => {
        if (!cancelled) setCellars([cellarEntry(person.id, true, profile)]);
      });
    return () => {
      cancelled = true;
    };
  }, [storage, person, profileReady, generation]);

  useEffect(() => {
    if (person.role !== 'owner' || !profileReady) return;
    let cancelled = false;
    void listCellars(storage, person.id)
      // Without the list the owner still has their own cellar; the others appear on retry.
      .catch(() => own)
      .then((list) => {
        if (!cancelled) setCellars(list);
      });
    return () => {
      cancelled = true;
    };
  }, [storage, person, own, profileReady, generation]);

  const refresh = useCallback(() => setGeneration((n) => n + 1), []);

  return (
    <CellarListContext.Provider value={{ cellars, refresh }}>{children}</CellarListContext.Provider>
  );
}

export function useCellarList(): CellarListValue {
  const value = useContext(CellarListContext);
  if (!value) {
    throw new Error('useCellarList() must be used within a <CellarListProvider>');
  }
  return value;
}

const ActiveCellarContext = createContext<CellarEntry | undefined>(undefined);

/** Names the cellar the screens inside are showing (set by the `/c/:cellarId` route). */
export const ActiveCellarProvider = ActiveCellarContext.Provider;

export function useActiveCellar(): CellarEntry {
  const value = useContext(ActiveCellarContext);
  if (!value) {
    throw new Error('useActiveCellar() must be used within an <ActiveCellarProvider>');
  }
  return value;
}
